import type { Directive } from 'vue';

interface LightboxBindingValue
{
    inactive?: boolean;
    dbClick?: boolean;
}

interface LightboxElement extends HTMLElement
{
    lightboxClickListener?: (e: MouseEvent) => void;
}

interface LightboxOverlay extends HTMLElement
{
    closeLightbox?: () => void;
}

const lightboxOverlayClasses = 'fixed inset-0 z-4 flex items-center justify-center bg-black/80 p-[clamp(1rem,4vw,3rem)]';
const lightboxImageClasses = 'max-h-full max-w-full rounded-md bg-white object-contain shadow-2xl shadow-black/35';
const lightboxCloseClasses = 'absolute end-4 top-4 flex size-10 cursor-pointer items-center justify-center rounded-full border border-white/35 bg-black/45 text-2xl/none text-white hover:bg-white/15 dark:text-neutral-200';

function currentLightbox()
{
    return document.querySelector<LightboxOverlay>('[data-edito-lightbox="true"]');
}

function closeCurrentLightbox()
{
    currentLightbox()?.closeLightbox?.();
}

function lightboxSource(el: HTMLElement)
{
    return el.dataset.src || null;
}

function showLightbox(src: string)
{
    closeCurrentLightbox();

    const previousOverflow = document.body.style.overflow;
    const overlay = document.createElement('div') as LightboxOverlay;
    const image = document.createElement('img');
    const closeButton = document.createElement('button');

    overlay.className = lightboxOverlayClasses;
    overlay.dataset.editoLightbox = 'true';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    image.className = lightboxImageClasses;
    image.src = src;
    image.alt = '';

    closeButton.className = lightboxCloseClasses;
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', 'Zamknij podgląd');
    closeButton.textContent = '×';

    function close()
    {
        document.removeEventListener('keydown', onKeyDown);
        document.body.style.overflow = previousOverflow;
        overlay.remove();
    }

    function onKeyDown(e: KeyboardEvent)
    {
        if (e.key === 'Escape')
        {
            close();
        }
    }

    overlay.closeLightbox = close;
    overlay.addEventListener('click', close);
    image.addEventListener('click', e => e.stopPropagation());
    closeButton.addEventListener('click', close);
    document.addEventListener('keydown', onKeyDown);

    overlay.append(image, closeButton);
    document.body.append(overlay);
    document.body.style.overflow = 'hidden';
}

export default defineNuxtPlugin({
    name: 'directives',
    dependsOn: [],
    async setup(nuxtApp)
    {
        const app = nuxtApp.vueApp;

        app.directive('slugify', {
            beforeMount: (el: any, binding: any, vnode: any) =>
            {
                el.slugify = function (value: string)
                {
                    return slugify(value);
                };

                el.addEventListener('blur', function (e: any)
                {
                    e.target.value = el.slugify(e.target.value);
                    e.target.dispatchEvent(new Event('input', { bubbles: true }));
                });
            }
        } as Directive);

        app.directive('focus', {
            mounted: function (el: HTMLElement)
            {
                el.dataset.focus = 'true';
                el.focus();
            }
        } as Directive);

        app.directive('lightbox', {
            mounted: function (el: LightboxElement, binding: { value?: LightboxBindingValue })
            {
                if (binding.value?.inactive)
                    return;

                const dbClick = binding.value?.dbClick ?? false;
                const mouseClick = useMouseClick();

                el.lightboxClickListener = (e: MouseEvent) =>
                {
                    const src = lightboxSource(el);

                    if (src)
                    {
                        e.preventDefault();
                        e.stopPropagation();

                        mouseClick(
                            () =>
                            {
                                if (!dbClick)
                                {
                                    showLightbox(src);
                                }
                            },
                            () =>
                            {
                                if (dbClick)
                                {
                                    showLightbox(src);
                                }
                            }
                        );
                    }
                };

                el.addEventListener('click', el.lightboxClickListener);
            },
            beforeUnmount: function (el: LightboxElement)
            {
                if (el.lightboxClickListener)
                {
                    el.removeEventListener('click', el.lightboxClickListener);
                }
            }
        });
    }
});
